import { createColumnHelper } from '@tanstack/react-table';
import { Users } from 'lucide-react';

import { notifySuccess } from '@/portainer/services/notifications';
import { useUserMembership } from '@/portainer/users/queries';
import { UserId } from '@/portainer/users/types';
import { useTeams } from '@/react/portainer/users/teams/queries';
import { TeamRole } from '@/react/portainer/users/teams/types';
import { usePublicSettings } from '@/react/portainer/settings/queries';

import { Datatable } from '@@/datatables';
import { DeleteButton } from '@@/buttons/DeleteButton';
import { createPersistedStore } from '@@/datatables/types';
import { useTableState } from '@@/datatables/useTableState';
import { Badge } from '@@/Badge';
import { Link } from '@@/Link';
import { TextTip } from '@@/Tip/TextTip';

import { useRemoveUserMembershipsMutation } from './queries/useRemoveUserMembershipsMutation';
import { AddToTeamForm } from './AddToTeamForm';

interface TeamRow {
  id: number;
  teamId: number;
  name: string;
  role: TeamRole;
}

const columnHelper = createColumnHelper<TeamRow>();

const columns = [
  columnHelper.accessor('name', {
    header: 'Team',
    cell: ({ getValue, row }) => (
      <Link
        to="portainer.teams.team"
        params={{ id: row.original.teamId }}
        data-cy={`user-team-link-${getValue()}`}
      >
        {getValue()}
      </Link>
    ),
  }),
  columnHelper.accessor('role', {
    header: 'Team role',
    cell: ({ getValue }) =>
      getValue() === TeamRole.Leader ? (
        <Badge type="info">Leader</Badge>
      ) : (
        <Badge type="muted">Member</Badge>
      ),
  }),
];

const tableKey = 'user-teams';
const store = createPersistedStore(tableKey);

export function TeamsTab({
  userId,
  username,
  userIsAdmin,
  canEdit,
}: {
  userId: UserId;
  username: string;
  userIsAdmin: boolean;
  canEdit: boolean;
}) {
  const membershipsQuery = useUserMembership(userId);
  const teamsQuery = useTeams();
  const removeMutation = useRemoveUserMembershipsMutation(userId);
  const tableState = useTableState(store, tableKey);
  const teamSyncQuery = usePublicSettings<boolean>({
    select: (settings) => settings.TeamSync,
  });

  const teamSyncOff = teamSyncQuery.data === false;
  const canManageMemberships = canEdit && teamSyncOff;

  const teams = teamsQuery.data ?? [];
  const teamNames = new Map(teams.map((team) => [team.Id, team.Name]));
  const memberships = membershipsQuery.data ?? [];
  const joined = new Set(memberships.map((membership) => membership.TeamID));

  const dataset: Array<TeamRow> = memberships.map((membership) => ({
    id: membership.Id,
    teamId: membership.TeamID,
    name: teamNames.get(membership.TeamID) ?? `Team ${membership.TeamID}`,
    role: membership.Role,
  }));

  return (
    <Datatable
      columns={columns}
      dataset={dataset}
      isLoading={membershipsQuery.isLoading || teamsQuery.isLoading}
      settingsManager={tableState}
      title="Teams"
      titleIcon={Users}
      emptyContentLabel="This user is not in any team."
      getRowId={(row) => String(row.id)}
      disableSelect={!canManageMemberships}
      renderTableActions={(selectedItems) =>
        canManageMemberships && (
          <DeleteButton
            disabled={selectedItems.length === 0}
            isLoading={removeMutation.isLoading}
            confirmMessage={`Remove ${username} from the selected team(s)? Any access they had through those teams goes with them.`}
            onConfirmed={() => handleRemove(selectedItems)}
            data-cy="user-teams-remove-button"
          />
        )
      }
      description={canEdit && renderAddControl()}
      data-cy="user-teams-datatable"
    />
  );

  function renderAddControl() {
    if (teamSyncQuery.data === undefined) {
      return null;
    }

    if (teamSyncQuery.data) {
      return (
        <TextTip color="orange">
          Team memberships are managed by the external authentication provider
          as team sync is currently enabled.
        </TextTip>
      );
    }

    if (userIsAdmin) {
      return (
        <TextTip color="orange">
          Administrators can&apos;t be added to teams as they already have
          access to every environment.
        </TextTip>
      );
    }

    return (
      <AddToTeamForm
        userId={userId}
        teams={teams.filter((team) => !joined.has(team.Id))}
      />
    );
  }

  function handleRemove(rows: Array<TeamRow>) {
    removeMutation.mutate(
      rows.map((row) => row.id),
      {
        onSuccess() {
          notifySuccess('Success', 'User removed from team(s)');
        },
      }
    );
  }
}
